import { Link, useRouterState } from '@tanstack/react-router'
import { AlertTriangle, Plus, Server, Settings } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { Button } from '@/components/shadcn/button'
import { useLayout } from '@/stores/layout'
import { RoleGate } from '@/components/RoleGate'
import { useOverview, useProjects } from './useOverview'
import { CreateProjectButton } from '@/features/projects/CreateProject'
import { DeployOne } from '@/features/servers/FleetNotice'

function NavItem({ to, icon, label, count, tone }: { to: string; icon: ReactNode; label: string; count?: number; tone?: 'err' | 'warn' }) {
  const path = useRouterState({ select: (s) => s.location.pathname })
  const active = to === '/' ? path === '/' : path.startsWith(to)
  return (
    <Link to={to} className={cn('flex h-8 items-center gap-2 rounded-md px-2 text-sm outline-none transition-colors focus-visible:bg-surface-2', active ? 'bg-surface-3 text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg')}>
      <span className="text-fg-subtle">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {!!count && <span className={cn('text-2xs tabular-nums', tone === 'err' ? 'text-err' : tone === 'warn' ? 'text-warn' : 'text-fg-subtle')}>{count}</span>}
    </Link>
  )
}

export function Sidebar() {
  const { data } = useOverview()
  const { sidebarWidth } = useLayout()
  const path = useRouterState({ select: (s) => s.location.pathname })

  return (
    <nav style={{ width: sidebarWidth }} className="flex h-full shrink-0 flex-col bg-surface-1" aria-label="Main">
      <div className="flex h-11 items-center px-3">
        <div className="flex items-center gap-2 font-semibold">
          <DeployOne />
          <Link to="/">Deployment</Link>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        <div className="mb-1 flex h-6 items-center justify-between px-2">
          <span className="text-xs font-medium text-fg-subtle">Projects</span>
          <RoleGate action="projects.create">
            <CreateProjectButton trigger={<Button variant="ghost" size="icon-xs" className="size-4 rounded-xs text-fg-subtle hover:bg-hover" aria-label="New project"><Plus size={12} /></Button>} />
          </RoleGate>
        </div>
        {!data && <div className="space-y-1 px-2">{[0, 1, 2].map((i) => <div key={i} className="h-6 animate-pulse rounded-xs bg-surface-2" />)}</div>}
        {data?.projects.length === 0 && <div className="px-2 py-1 text-xs text-fg-faint">No projects yet.</div>}
        {data?.projects.map((p, i) => {
          const activeProject = path.startsWith(`/p/${p.key}`)
          // Client projects first; ours follow under their own heading.
          const firstInternal = p.internal && !data.projects[i - 1]?.internal
          return (
            <div key={p.key}>
              {firstInternal && <div className="mt-3 mb-1 flex h-6 items-center px-2 text-xs font-medium text-fg-subtle">Internal</div>}
              {/* The whole row is the link: selected is a grey row, no focus box or count. */}
              <Link to="/p/$project" params={{ project: p.key }}
                className={cn('flex h-8 items-center gap-1 rounded-md px-2 text-sm outline-none transition-colors focus-visible:bg-surface-2', activeProject ? 'bg-surface-3 text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg')}>
                <span className="flex-1 truncate">{p.name}</span>
                {p.repoState !== 'ok' && (
                  <span className="flex items-center gap-0.5 text-2xs text-warn" title="Repo missing — commits stay local">
                    <AlertTriangle size={10} /> repo
                  </span>
                )}
              </Link>
            </div>
          )
        })}
      </div>
      <div className="border-t border-border p-2">
        <RoleGate action="servers.manage">
          <NavItem to="/servers" icon={<Server size={14} />} label="Servers" />
        </RoleGate>
        <NavItem to="/settings" icon={<Settings size={14} />} label="Settings" />
      </div>
    </nav>
  )
}
